<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
          v-if="isVisible"
          class="modal-overlay"
          :class="{ 'modal-overlay--centered': centered }"
          @click="handleOverlayClick"
      >
        <div
            class="modal-content"
            :class="{ 'closing': isClosing, withImage: withImage, 'modal-content--centered': centered }"
            @click.stop
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            :style="contentStyleWithBackground"
        >
          <div class="modal-header">
            <div class="drag-handle"></div>
          </div>
          <div class="modal-body">
            <slot></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted} from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  background: {
    type: String,
    default: '#FFFFFF'
  },
  withImage: {
    type: Boolean,
    default: false
  },
  noClose: {
    type: Boolean,
    default: false
  },
  centered: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const isVisible = ref(false)
const isClosing = ref(false)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)
const dragDistance = ref(0)

const contentStyleWithBackground = computed(() => {
  const bg = props.background || '#FFFFFF'
  if (isDragging.value) {
    const translateY = Math.max(0, dragDistance.value)
    return {
      background: bg,
      transform: `translateY(${translateY}px)`,
      transition: 'none'
    }
  }
  return {
    background: bg
  }
})

const handleOverlayClick = (e) => {
  if (props.noClose) return
  if (e.target.classList.contains('modal-overlay')) {
    closeModal()
  }
}

const handleTouchStart = (e) => {
  touchStartY.value = e.touches[0].clientY
  isDragging.value = true
}

const handleTouchMove = (e) => {
  if (!isDragging.value) return

  touchCurrentY.value = e.touches[0].clientY
  dragDistance.value = touchCurrentY.value - touchStartY.value

  if (dragDistance.value > 0) {
    e.preventDefault()
  }
}

const handleTouchEnd = () => {
  if (!isDragging.value) return

  if (props.noClose) {
    resetDrag()
    isDragging.value = false
    dragDistance.value = 0
    return
  }

  const swipeThreshold = 100
  const velocityThreshold = 0.3

  const swipeDistance = dragDistance.value
  const swipeDuration = 300
  const swipeVelocity = swipeDistance / swipeDuration

  if (swipeDistance > swipeThreshold || swipeVelocity > velocityThreshold) {
    closeModal()
  } else {
    resetDrag()
  }

  isDragging.value = false
  dragDistance.value = 0
}

const resetDrag = () => {
  dragDistance.value = 0
}

const openModal = () => {
  isVisible.value = true
  isClosing.value = false
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  isClosing.value = true
  setTimeout(() => {
    isVisible.value = false
    isClosing.value = false
    emit('update:modelValue', false)
    emit('close')
    document.body.style.overflow = ''
  }, 300)
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    openModal()
  } else if (isVisible.value) {
    closeModal()
  }
}, { immediate: true })

const handleKeydown = (e) => {
  if (props.noClose) return
  if (e.key === 'Escape' && isVisible.value) {
    closeModal()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000000BF;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
}

.modal-overlay--centered {
  align-items: center;
  padding: 16px;
}

.modal-content {
  background: white;
  width: 100%;
  max-width: 500px;
  max-height: calc(100dvh - max(24px, env(safe-area-inset-top, 0px)) - max(24px, env(safe-area-inset-bottom, 0px)));
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  transform: translateY(0);
  transition: transform 0.3s ease-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-content--centered {
  border-radius: 16px;
}

.withImage {
  position: relative;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: url("@/assets/images/storm-back.png");
    background-repeat: no-repeat;
    background-size: cover;
    inset: 0;
    z-index: -1;
  }
}

.modal-header {
  padding: 8px 0 11px;
  display: flex;
  justify-content: center;
}

.drag-handle {
  width: 48px;
  height: 5px;
  background-color: #CDCFD0;
  border-radius: 100px;
}

.modal-body {
  padding: 0 24px 24px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease-out;
}

.modal-enter-from .modal-content {
  transform: translateY(100%);
}

.modal-leave-to .modal-content {
  transform: translateY(100%);
}

.modal-content.closing {
  transform: translateY(100%);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
